import { t } from '@lingui/core/macro';
import {
    EmsRfqUpdateDTO,
    ProjectArea,
    ProjectProgress,
    RfqContext,
    RfqStatus,
    RfqUpdateTrigger,
} from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import { compareByDate } from '@luminovo/commons';
import {
    analytics,
    httpQueryKey,
    invalidateAllQueriesForEndpoint,
    useDebugErrorHandler,
    useHttpQuery,
    useSuspenseHttpQuery,
} from '@luminovo/data-stack';
import { useNavigate } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';
import { http } from '@luminovo/http-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';

import { useHttpMutation } from '@/shared/data/mutation/useHttpMutation';
import { useContributors } from '@/shared/data/user/userHandler';

import { rfqWarningTranslations } from './i18n';

export function useRfQSuspense(rfqId: string) {
    return useSuspenseHttpQuery(
        'GET /rfqs/:rfqId',
        {
            pathParams: { rfqId },
        },
        {
            select: (res) => res.data,
        },
    );
}

export function useRfQ(rfqId: string, enabled: boolean = true) {
    return useHttpQuery(
        'GET /rfqs/:rfqId',
        {
            pathParams: { rfqId },
        },
        {
            enabled,
            select: (res) => res.data,
        },
    );
}

export function useForcedOfferUpdate(rfqId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await http(
                'POST /rfqs/:rfqId/update-offers',
                {
                    pathParams: { rfqId },
                    requestBody: { since: new Date().toISOString() },
                },
                getToken(),
            );
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: httpQueryKey('GET /assemblies/:assemblyId/descendants'),
                }),
                queryClient.invalidateQueries({
                    queryKey: httpQueryKey('POST /sourcing-scenarios/bulk'),
                }),
                queryClient.invalidateQueries({
                    queryKey: httpQueryKey('GET /sourcing/progress/:rfqId'),
                }),
                queryClient.invalidateQueries({
                    queryKey: httpQueryKey('POST /assemblies/:id/pcb/:pcbId/offer-state'),
                }),
            ]);
        },
    });
}

export function useMutationUpdateRfq(rfqId: string) {
    const { mutateAsync, checkPermission } = useHttpMutation('PATCH /rfqs/:rfqId', {
        snackbarMessage: t`Project updated successfully`,
    });

    return {
        mutateAsync: async ({ rfq }: { rfq: EmsRfqUpdateDTO }) => {
            return await mutateAsync({
                pathParams: { rfqId },
                requestBody: { type: 'Ems', data: rfq },
            });
        },
        checkPermission,
    };
}

export function useMutationAddUserAsContributor(rfqId: string) {
    const queryClient = useQueryClient();
    const onError = useDebugErrorHandler();

    const { data: contributorsData } = useContributors(rfqId);
    const contributors = [...(contributorsData?.external ?? []), ...(contributorsData?.internal ?? [])];
    const contributorIds = contributors.map((c) => c.id);

    return useMutation({
        mutationFn: async ({ userId }: { userId: string }) => {
            await http(
                'PATCH /contributors/rfqs/:rfqId',
                {
                    pathParams: { rfqId },
                    requestBody: Array.from(new Set([userId, ...contributorIds])),
                },
                getToken(),
            );
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: httpQueryKey('GET /contributors/rfqs/:rfqId'),
            });
        },
        onError,
    });
}

export function useMutationRequestQuotation(rfqId: string) {
    return useHttpMutation(`PATCH /rfqs/:rfqId/request-quotation`, {
        snackbarMessage: t`Quotation requested`,
        onSuccess: async () => {
            analytics.track('request_quotation', {
                rfq_id: rfqId,
            });
        },
    });
}

export function useRfqs({ customerId }: { customerId?: string } = {}) {
    return useSuspenseHttpQuery(
        'GET /rfqs',
        {
            queryParams: customerId ? { filter: 'all', customer_id: customerId } : { filter: 'all' },
        },
        {
            select: (res) => Array.from(res.data).sort(compareByDate((a) => a.creation_date)),
        },
    );
}

export function useRfqListItem(id: string = '') {
    return useHttpQuery(
        'GET /rfqs',
        { queryParams: { filter: 'all' } },
        {
            select: (res) => res.data.find((rfq) => rfq.id === id),
            enabled: Boolean(id),
        },
    );
}

export function useMutationUpdateRfqStatus(rfqId: string) {
    const { mutateAsync, checkPermission } = useHttpMutation('PATCH /rfqs/:rfqId', {
        snackbarMessage: t`Project status updated successfully`,
    });

    return {
        mutateAsync: async ({
            status,
            shippingTrackingLink,
            trigger,
        }: {
            status: RfqStatus;
            shippingTrackingLink?: string | undefined;
            trigger?: RfqUpdateTrigger | undefined;
        }) => {
            return await mutateAsync({
                pathParams: { rfqId },
                requestBody: {
                    type: 'Ems',
                    data: {
                        status: status,
                        shipping_tracking_link:
                            shippingTrackingLink && shippingTrackingLink.length > 0 ? shippingTrackingLink : null,
                        trigger: trigger,
                    },
                },
            });
        },
        checkPermission,
    };
}

export function useMutationUpdateProjectProgress(rfqId: string) {
    const { mutateAsync, checkPermission } = useHttpMutation('PATCH /rfqs/:rfqId/project-progress', {
        snackbarMessage: t`Project progress updated successfully`,
    });

    return {
        mutateAsync: async ({ area, progress }: { area: ProjectArea; progress: ProjectProgress }) => {
            return await mutateAsync({
                pathParams: { rfqId },
                requestBody: { area, progress },
            });
        },
        checkPermission,
    };
}

export function useProjectProgressHistory() {
    return useHttpQuery('GET /rfqs/project-progress/history', {});
}

export function useQueryProjectProgress() {
    return useHttpQuery('GET /rfqs/project-progress', {});
}

export function useQueryProjectProgressForRfq(rfqId: string) {
    return useHttpQuery('GET /rfqs/:rfqId/project-progress', {
        pathParams: { rfqId },
    });
}

export function useMutationArchiveRfq(rfqId: string, archive: boolean) {
    const endpoint = archive ? 'PATCH /rfqs/:rfqId/archive' : 'PATCH /rfqs/:rfqId/unarchive';
    const message = archive ? t`Project archived successfully` : t`Project unarchived successfully`;

    const { mutateAsync, checkPermission } = useHttpMutation(endpoint, {
        snackbarMessage: message,
    });

    return {
        mutateAsync: async () => {
            await mutateAsync({ pathParams: { rfqId } });
        },
        checkPermission,
    };
}

export const outsideRfQContext: RfqContext = { type: 'OutsideRfQ' };

export function useRequestTechnicalSupport(rfqId: string) {
    const queryClient = useQueryClient();
    const onError = useDebugErrorHandler();

    return useMutation({
        mutationFn: async () => {
            await http('POST /rfqs/:rfqId/technical-support-request', { pathParams: { rfqId } }, getToken());
        },
        onSuccess: async () => {
            await invalidateAllQueriesForEndpoint('GET /rfqs/:rfqId', queryClient);
        },
        onError,
    });
}

export function useRequestEditQuotation(rfqId: string) {
    const queryClient = useQueryClient();
    const onError = useDebugErrorHandler();

    return useMutation({
        mutationFn: async () => {
            await http('POST /rfqs/:rfqId/edit-quotation-request', { pathParams: { rfqId } }, getToken());
        },
        onSuccess: async () => {
            await invalidateAllQueriesForEndpoint('GET /rfqs/:rfqId', queryClient);
        },
        onError,
    });
}

export function useDuplicateRfq({ rfqId, onSuccess }: { rfqId: string; onSuccess?: () => void }) {
    const navigate = useNavigate();
    const onError = useDebugErrorHandler();
    const snackbar = useSnackbar();

    return useMutation({
        mutationFn: async (newRfqName?: string) => {
            const response = await http(
                'POST /assemblies/:id/duplicate',

                {
                    pathParams: { id: rfqId },
                    requestBody: { new_rfq_name: newRfqName },
                },
                getToken(),
            );
            return response;
        },
        onSuccess: async ({ rfq_id: rfqId, warning }) => {
            if (onSuccess) {
                onSuccess();
            }

            if (warning) {
                const localized = transEnum(warning, rfqWarningTranslations);
                snackbar.enqueueSnackbar(localized, { variant: 'warning' });
            }

            navigate({ to: '/projects/$projectId/dashboard', params: { projectId: rfqId } });
        },
        onError: async (e) => {
            onError(e);
        },
    });
}

export function useCustomerPortalState(rfqId: string) {
    const queryResult = useHttpQuery(
        'POST /rfqs/:rfqId/customer-portal',
        { pathParams: { rfqId } },
        { refetchOnMount: 'always' },
    );

    const customerPortalState = queryResult.data;

    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: httpQueryKey('GET /rfqs/:rfqId', { pathParams: { rfqId } }),
        });
        queryClient.invalidateQueries({ queryKey: httpQueryKey('GET /rfqs') });
    }, [customerPortalState, queryClient, rfqId]);

    return queryResult;
}
